"use client";

import { useUser } from "@clerk/nextjs";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import {
  ArrowRight,
  Bot,
  Brain,
  Briefcase,
  Cpu,
  Eye,
  Hash,
  Layers,
  MessageSquare,
  Plug,
  ShieldCheck,
  Sparkles,
  Zap,
} from "lucide-react";
import { ScoutLogoMark } from "@/components/scout-logo-mark";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const heroSignals = [
  "Lives in Slack — no new app to open",
  "Remembers what they're working on",
  "Plugs into the tools you already use",
  "Runs on your server, your data, your control",
];

const integrations = ["Slack", "Linear", "Composio", "Scout"];
const signInHref = "/auth/sign-in?redirect_url=%2Foverview";
const signUpHref = "/auth/sign-up?redirect_url=%2Foverview";

const loopSteps = [
  {
    number: "01",
    title: "Set up your team",
    description:
      "Tell us who's on the team and who owns what. No new system to learn — just the team you already run.",
  },
  {
    number: "02",
    title: "Connect your tools",
    description:
      "Plug in Slack and your project tracker — like Linear or similar. We handle the auth so the helpers can see the work.",
  },
  {
    number: "03",
    title: "We bring it online",
    description:
      "We spin up a helper for each person and put it in Slack. No new app, no new login. Ready the same day.",
  },
  {
    number: "04",
    title: "Hand it off",
    description:
      "Each person talks to their helper in Slack. It remembers what they're working on — every chat, every day.",
  },
];

const capabilities = [
  {
    icon: Layers,
    title: "Same setup, every team",
    description:
      "One setup that works for every team, every site, every shift. Roll it out to the next group the same way you rolled out the first.",
  },
  {
    icon: MessageSquare,
    title: "Lives in Slack",
    description:
      "Each helper sits in Slack — where your team already works. No new app to open, no new tab to keep track of.",
  },
  {
    icon: Brain,
    title: "Remembers what they're working on",
    description:
      "Every helper keeps context across days and weeks. People don't have to repeat themselves to get a useful answer.",
  },
  {
    icon: Briefcase,
    title: "Reads from your tracker",
    description:
      "Pulls open items straight from your project tracker so nobody has to forward, copy, or remind. Less chasing, more follow-through.",
  },
  {
    icon: ShieldCheck,
    title: "Watches first, acts when you say so",
    description:
      "Every helper starts read-only. You decide which ones can take action and when — no surprises, no overreach.",
  },
  {
    icon: Cpu,
    title: "Runs on your server",
    description:
      "Your server, your data, your control. No lock-in to one vendor and no surprise migrations down the road.",
  },
];

const beforeAfter = [
  [
    "Before",
    "Dropped follow-ups, repeated context in three threads, and people chasing each other for status.",
  ],
  [
    "After",
    "Each person has one always-on helper in Slack that remembers what they're working on and reads the same tracker the team does.",
  ],
  [
    "Result",
    "Hours back every week, fewer balls through the cracks, and customers who hear from you on time.",
  ],
];

export default function HomePage() {
  const { isLoaded, isSignedIn } = useUser();
  const router = useRouter();

  useEffect(() => {
    if (isLoaded && isSignedIn) router.replace("/overview");
  }, [isLoaded, isSignedIn, router]);

  if (isSignedIn) return <main className="min-h-screen" />;

  return (
    <div className="relative min-h-screen overflow-hidden bg-background text-foreground">
      {/* Ambient background */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
      >
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,color-mix(in_srgb,var(--primary)_5%,transparent),transparent_24rem)]" />
        <div className="absolute inset-0 opacity-[0.035] [background-image:radial-gradient(circle_at_1px_1px,currentColor_1px,transparent_0)] [background-size:18px_18px]" />
        <div className="absolute inset-x-0 top-0 h-px bg-border/70" />
      </div>

      {/* Header */}
      <header className="relative">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 sm:py-5">
          <a href="/" className="flex items-center gap-2.5">
            <ScoutLogoMark size="lg" />
            <span className="font-medium">Scout</span>
          </a>
          <nav className="hidden items-center gap-6 text-sm text-muted-foreground sm:flex">
            <a href="#how" className="transition-colors hover:text-foreground">
              How it works
            </a>
            <a
              href="#capabilities"
              className="transition-colors hover:text-foreground"
            >
              Capabilities
            </a>
            <a
              href="#who"
              className="transition-colors hover:text-foreground"
            >
              Who it's for
            </a>
          </nav>
          <Button asChild size="sm" variant="ghost" className="text-sm">
            <a href={signInHref}>
              Sign in
              <ArrowRight className="ml-1 size-3.5" />
            </a>
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 pb-14 pt-8 sm:px-6 sm:pb-20 sm:pt-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:items-center lg:gap-14 lg:pt-16">
          <div>
            <Badge
              variant="accent"
              className="px-3 py-1 font-mono text-[0.68rem] uppercase tracking-[0.18em]"
            >
              <Sparkles className="mr-1.5 size-3" />
              Always-on helpers
            </Badge>

            <h1 className="mt-6 max-w-2xl text-balance text-[2.35rem] font-medium leading-[1.04] sm:text-5xl lg:text-[4rem]">
              An always-on helper for{" "}
              <span className="text-primary">every person</span>{" "}
              on your team.
            </h1>

            <p className="mt-6 max-w-xl text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">
              Each person on your team gets their own helper in Slack — plugged
              into the tools you already use, picking up where the last
              conversation left off.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3 sm:mt-10">
              <Button asChild size="lg">
                <a href={signUpHref}>
                  Get started
                  <ArrowRight className="ml-1 size-4" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="#how">
                  See how it works
                  <ArrowRight className="ml-1 size-4" />
                </a>
              </Button>
            </div>

            <ul className="mt-9 grid gap-2.5 sm:mt-11 sm:grid-cols-2">
              {heroSignals.map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-3 rounded-lg border border-border/70 bg-card/55 px-3.5 py-3 text-sm text-muted-foreground"
                >
                  <span className="grid size-6 shrink-0 place-items-center rounded-md bg-primary/[0.08] text-primary">
                    <Zap className="size-3" />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Mockup */}
          <div className="relative">
            <div className="relative overflow-hidden rounded-lg border border-border bg-card/90 shadow-sm">
              {/* Window chrome */}
              <div className="flex items-center justify-between gap-3 border-b border-border/80 px-4 py-3.5 sm:px-5">
                <div className="flex items-center gap-2">
                  <span className="size-2.5 rounded-full bg-rose-400/70" />
                  <span className="size-2.5 rounded-full bg-amber-400/70" />
                  <span className="size-2.5 rounded-full bg-emerald-400/70" />
                </div>
                <div className="hidden items-center gap-2 rounded-md bg-muted px-3 py-1 font-mono text-[0.68rem] uppercase tracking-[0.14em] text-muted-foreground sm:flex">
                  <Hash className="size-3" />
                  marcy — agent
                </div>
                <div className="flex items-center gap-1.5 rounded-md border border-[color-mix(in_srgb,var(--agent-success)_28%,transparent)] bg-[color-mix(in_srgb,var(--agent-success)_10%,transparent)] px-2.5 py-1 font-mono text-[0.6rem] uppercase tracking-[0.14em] text-[var(--agent-success)]">
                  <span className="size-1.5 rounded-full bg-[var(--agent-success)]" />
                  Online
                </div>
              </div>

              {/* Conversation */}
              <div className="space-y-4 px-4 py-5 sm:px-5 sm:py-6">
                <Bubble who="you" name="Marcy">
                  Draft a follow-up on the Acme quote — we haven't heard back since Tuesday.
                </Bubble>
                <Bubble who="agent" name="Scout · marcy">
                  <span className="text-foreground">
                    Found it in your tracker — Acme quote opened March 4. I
                    drafted a follow-up here in Slack referencing their March
                    RFQ. Want me to post the draft for your review?
                  </span>
                </Bubble>
                <Bubble who="agent" name="Scout · marcy">
                  Holding for your sign-off. Read-only until you say go — I'll
                  remember this thread tomorrow.
                </Bubble>
              </div>

              {/* Status strip */}
              <div className="grid grid-cols-3 gap-px border-t border-border bg-border/60">
                {[
                  ["Connected", "Slack + tracker", Plug],
                  ["Memory", "every chat", Brain],
                  ["Mode", "read-only", Eye],
                ].map(([label, value, Icon]) => {
                  const Glyph = Icon as typeof Plug;
                  return (
                    <div key={label as string} className="bg-card/95 px-3 py-3 sm:px-4">
                      <div className="flex items-center gap-1.5 font-mono text-[0.6rem] uppercase tracking-[0.14em] text-muted-foreground">
                        <Glyph className="size-3" />
                        {label as string}
                      </div>
                      <div className="mt-1.5 text-sm font-medium">
                        {value as string}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Integration strip */}
        <div className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 sm:pb-12">
          <div className="flex flex-wrap items-center justify-between gap-5 rounded-lg border border-border/70 bg-card/55 px-4 py-4 sm:px-5">
            <div>
              <p className="font-mono text-[0.62rem] uppercase tracking-[0.18em] text-muted-foreground">
                Works with the tools you already use
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {integrations.map((name) => (
                <div
                  key={name}
                  className="flex items-center gap-2 rounded-md border border-border/70 bg-background/55 px-3 py-1.5 text-sm font-medium"
                >
                  <span className="size-1.5 rounded-full bg-primary" />
                  {name}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="relative border-y border-border/70 bg-card/30">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="font-mono text-[0.68rem] uppercase tracking-[0.18em] text-primary">
                The loop
              </p>
              <h2 className="mt-4 max-w-2xl text-balance text-3xl font-medium leading-tight sm:text-4xl">
                Set it up. Plug it in. Let it work.
              </h2>
            </div>
            <p className="max-w-md text-base leading-7 text-muted-foreground">
              One short setup, your existing tools, and a helper for each person
              on the team. You stay in control of what it can do.
            </p>
          </div>

          <ol className="mt-10 grid gap-4 sm:mt-14 lg:grid-cols-4">
            {loopSteps.map((step) => (
              <li
                key={step.number}
                className="group relative rounded-lg border border-border bg-card/80 p-4 transition-colors hover:border-primary/35 sm:p-5"
              >
                <div className="font-mono text-xs uppercase tracking-[0.18em] text-primary/80">
                  {step.number}
                </div>
                <h3 className="mt-5 text-lg font-medium">
                  {step.title}
                </h3>
                <p className="mt-3 text-sm leading-7 text-muted-foreground">
                  {step.description}
                </p>
                <div
                  aria-hidden
                  className="absolute inset-x-6 bottom-0 h-px bg-gradient-to-r from-transparent via-primary/0 to-transparent transition-all group-hover:via-primary/40"
                />
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Capabilities */}
      <section id="capabilities" className="relative">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
          <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-12">
            <div>
              <p className="font-mono text-[0.68rem] uppercase tracking-[0.18em] text-primary">
                What you get
              </p>
              <h2 className="mt-4 max-w-md text-balance text-3xl font-medium leading-tight sm:text-4xl">
                Plain tools for the people doing the work.
              </h2>
              <p className="mt-6 max-w-md text-base leading-7 text-muted-foreground">
                A small set of features chosen for one job — give every person
                on your team a helper that's there when they need it and stays
                out of the way when they don't.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {capabilities.map((feature) => (
                <div
                  key={feature.title}
                  className="rounded-lg border border-border bg-card/80 p-4 transition-colors hover:border-primary/35 sm:p-5"
                >
                  <div className="grid size-10 place-items-center rounded-lg bg-primary/[0.08] text-primary">
                    <feature.icon className="size-5" />
                  </div>
                  <h3 className="mt-5 text-lg font-medium">
                    {feature.title}
                  </h3>
                  <p className="mt-2 text-sm leading-7 text-muted-foreground">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section id="who" className="relative border-t border-border/70 bg-card/30">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
          <div className="grid gap-6 lg:grid-cols-3">
            {beforeAfter.map(([label, copy]) => (
              <div
                key={label}
                className="rounded-lg border border-border bg-card/80 p-4 sm:p-6"
              >
                <div className="font-mono text-[0.68rem] uppercase tracking-[0.18em] text-primary">
                  {label}
                </div>
                <p className="mt-5 text-base leading-7 text-foreground sm:text-lg sm:leading-8">{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
          <div className="relative overflow-hidden rounded-lg border border-border bg-card/85 p-5 sm:p-8 lg:p-10">
            <div className="relative flex flex-col items-start gap-8 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="font-mono text-[0.68rem] uppercase tracking-[0.18em] text-primary">
                  Ready when you are
                </p>
                <h2 className="mt-4 max-w-2xl text-balance text-3xl font-medium leading-tight sm:text-4xl">
                  Give your team a helper that doesn't drop the ball.
                </h2>
                <p className="mt-5 max-w-xl text-base leading-7 text-muted-foreground">
                  Set it up once, plug in your tools, and each person on your
                  team has a helper in Slack the same day.
                </p>
              </div>
              <Button asChild size="lg">
                <a href={signUpHref}>
                  Get started
                  <ArrowRight className="ml-1 size-4" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-border/70">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-4 py-8 sm:flex-row sm:items-center sm:px-6 sm:py-10">
          <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
            <ScoutLogoMark size="xs" />
            <span>Scout</span>
            <span className="text-muted-foreground/50">·</span>
            <span>Studio Intrinsic</span>
          </div>
          <div className="font-mono text-[0.68rem] uppercase tracking-[0.18em] text-muted-foreground">
            For the people doing the work.
          </div>
        </div>
      </footer>
    </div>
  );
}

function Bubble({
  who,
  name,
  children,
}: {
  who: "you" | "agent";
  name: string;
  children: React.ReactNode;
}) {
  const isAgent = who === "agent";
  return (
    <div className="flex items-start gap-3">
      <span
        className={
          "mt-0.5 grid size-8 shrink-0 place-items-center rounded-md text-xs font-medium " +
          (isAgent
            ? "bg-primary/[0.08] text-primary"
            : "bg-muted text-muted-foreground")
        }
      >
        {isAgent ? <Bot className="size-4" /> : name.slice(0, 1)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{name}</span>
          {isAgent ? (
            <span className="rounded-md border border-primary/20 bg-primary/[0.08] px-1.5 py-0.5 font-mono text-[0.6rem] uppercase tracking-[0.14em] text-primary">
              Agent
            </span>
          ) : null}
          <span className="font-mono text-[0.6rem] uppercase tracking-[0.14em] text-muted-foreground">
            now
          </span>
        </div>
        <div className="mt-1 text-sm leading-6 text-muted-foreground">
          {children}
        </div>
      </div>
    </div>
  );
}
